import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faClipboard as farClipboard,
  faClock as farClock,
  faComment as farComment,
  faCopy as farCopy,
  faEnvelope as farEnvelope,
  faHourglass as farHourglass,
  faSmile as farSmile,
  faTrashAlt as farTrashAlt,
} from '@fortawesome/free-regular-svg-icons';
import {
  faAnchor,
  faAngleRight,
  faArrowCircleUp,
  faCalendar,
  faCamera,
  faCheck,
  faCheckCircle,
  faChevronDown,
  faChevronLeft,
  faChevronRight,
  faChevronUp,
  faCircle,
  faCog,
  faCopy,
  faEllipsisH,
  faEnvelope,
  faEye,
  faEyeSlash,
  faGift,
  faHeart,
  faImage,
  faLeaf,
  faList,
  faMap,
  faMapMarkerAlt,
  faMinus,
  faMinusCircle,
  faMobileAlt,
  faPencilAlt,
  faPhone,
  faPlayCircle,
  faPlus,
  faPlusSquare,
  faRocket,
  faSearch,
  faSeedling,
  faShare,
  faSort,
  faStar,
  faTags,
  faTimes,
  faTimesCircle,
  faTrash,
  faUser,
  faUserCircle,
  faUsers,
} from '@fortawesome/free-solid-svg-icons';
import { ThemeProvider, createTheme } from '@mui/material';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, Route, Switch, useHistory, useLocation } from 'react-router-dom';
import classes from './App.module.css';
import Layout from './hoc/Layout/Layout';
import * as actions from './store/actions';
import ComingSoon from 'components/ComingSoon/ComingSoon';
import Home from 'components/Home/Home';
import NoMatch from 'components/NoMatch/NoMatch';
import Privacy from 'components/StaticPages/Privacy';
import Terms from 'components/StaticPages/Terms';
import AddMobile from 'containers/Auth/AddMobile/AddMobile';
import CreateProfile from 'containers/Auth/CreateProfile/CreateProfile';
import Logout from 'containers/Auth/Logout/Logout';
import RequestPasswordReset from 'containers/Auth/PasswordReset/RequestPasswordReset/RequestPasswordReset';
import ResetPassword from 'containers/Auth/PasswordReset/ResetPassword/ResetPassword';
import SignIn from 'containers/Auth/SignIn/SignIn';
import SignUp from 'containers/Auth/SignUp/SignUp';
import Verify from 'containers/Auth/Verify/Verify';
import Growth from 'containers/Growth/Growth';
import Log from 'containers/Log/Log';
import Love from 'containers/Love/Love';
import ManageLogs from 'containers/ManageLogs/ManageLogs';
import MyAccount from 'containers/MyAccount/MyAccount';
import MyRiza from 'containers/MyRiza/MyRiza';
import Nest from 'containers/Nest/Nest';
import PostDetail from 'containers/Posts/PostDetail/PostDetail';
import Preview from 'containers/Posts/Preview/Preview';
import Help from 'containers/StaticPages/Help/Help';
import { ResourceCenter } from 'pages/ResourceCenter';
import { getBuildNumber } from 'shared/utility';
import { authCheckLogin, getAvailableServers } from 'store/thunks';

library.add(
  faAngleRight,
  faPlusSquare,
  faPlus,
  faMinus,
  faCalendar,
  faTimes,
  faMapMarkerAlt,
  faUserCircle,
  faRocket,
  faUsers,
  faUser,
  faTags,
  faChevronLeft,
  faChevronRight,
  faChevronDown,
  faChevronUp,
  faMap,
  faGift,
  faCheck,
  faCheckCircle,
  farComment,
  faCamera,
  faTrash,
  faAnchor,
  faPencilAlt,
  faEye,
  faEyeSlash,
  faMinusCircle,
  faSort,
  faCircle,
  faSearch,
  faCopy,
  farClipboard,
  farEnvelope,
  farCopy,
  faArrowCircleUp,
  farClock,
  farSmile,
  faEllipsisH,
  faTimesCircle,
  faList,
  faImage,
  farHourglass,
  faLeaf,
  faPlayCircle,
  faCog,
  farTrashAlt,
  faStar,
  faPhone,
  faEnvelope,
  faShare,
  faHeart,
  faSeedling,
  faMobileAlt,
);

const theme = createTheme();

const PrivateRoute = ({ component: Component, ...rest }) => {
  const { isAuthenticated, sessionTokenLoaded, UserId } = useSelector((state) => state.auth);
  return (
    <Route
      {...rest}
      render={(props) => {
        return sessionTokenLoaded && isAuthenticated && UserId && <Component {...props} />;
      }}
    />
  );
};

const PublicRoute = ({ component: Component, ...rest }) => {
  const { isAuthenticated } = useSelector((state) => state.auth);
  return (
    <Route
      {...rest}
      render={(props) => {
        return isAuthenticated ? <Redirect to="/my-riza" /> : <Component {...props} />;
      }}
    />
  );
};

const App = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { servers, loadingServers, scrollToTopOfChild } = useSelector((state) => state.app);
  const { sessionToken, isAuthenticated, ForceNewPassword, sessionTokenLoaded, UserId } = useSelector((state) => state.auth);
  const { signedInAs } = useSelector((state) => state.user);

  useEffect(() => {
    const accountNeedsVerification = localStorage.getItem('codeSentTo') === 'Email';
    if (accountNeedsVerification) {
      if (!signedInAs.UserId) {
        dispatch(authCheckLogin());
      }
    } else if (servers.length === 0) {
      dispatch(getAvailableServers());
    }
  }, [dispatch, servers.length, signedInAs.UserId]);

  useEffect(() => {
    if (servers.length === 0 && sessionToken && !loadingServers) {
      dispatch(getAvailableServers());
    }
  }, [dispatch, loadingServers, servers.length, sessionToken]);

  useEffect(() => {
    // Once user signsin, take them to '/create-profile'
    // otherwise, take them to '/my-riza'
    if (isAuthenticated && signedInAs.UserId && location.pathname === '/sign-in') {
      history.push('/my-riza');
    }
  }, [history, isAuthenticated, location.pathname, signedInAs.UserId]);

  useEffect(() => {
    if (ForceNewPassword === true) {
      history.push('/password-reset');
    }
  }, [ForceNewPassword, history]);

  useEffect(() => {
    // If a user hasn't yet created their profile, take them to '/create-profile'
    if (signedInAs.ProfileStatus === 'NotComplete' && !['/verify', '/add-mobile'].includes(location.pathname)) {
      history.push('/create-profile');
    }
  }, [history, location.pathname, signedInAs.ProfileStatus]);

  useEffect(() => {
    if (sessionTokenLoaded && isAuthenticated && UserId && location.pathname === '/') {
      history.replace('/my-riza');
    }
  }, [UserId, history, isAuthenticated, location.pathname, sessionTokenLoaded]);

  useEffect(() => {
    // Update usersnap with user and app version information once user information is available
    if (signedInAs.FirstName && signedInAs.UserId && window.Usersnap) {
      // Waiting on UserSnap to guide on how to pass user / appVer data to usersnap init
      // const user = signedInAs;
      const appVer = getBuildNumber();
      window.Usersnap.init({
        custom: {
          applicationVersion: appVer,
        },
        user: {
          userId: signedInAs.UserId,
          email: signedInAs.Email,
        },
      });
    }
  }, [signedInAs.Email, signedInAs.FirstName, signedInAs.UserId]);

  useEffect(() => {
    if (scrollToTopOfChild) {
      window.scrollTo(0, 0);
      dispatch(actions.updateScrollToTopOfChild(false));
    }
  }, [dispatch, scrollToTopOfChild]);

  useEffect(() => {
    // This gets around React Router taking user to same scroll place on page where they were before refreshing the screen.
    window.onbeforeunload = () => {
      window.scrollTo(0, 0);
    };
    return () => {
      window.onbeforeunload = undefined;
    };
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <div className={classes.AppContainer}>
        <Layout>
          <Switch>
            <Route path="/help" component={Help} />
            <Route path="/privacy" component={Privacy} />
            <Route path="/terms" component={Terms} />
            <PublicRoute path="/sign-up" exact component={SignUp} />
            <PublicRoute path="/sign-in" exact component={SignIn} />
            <PublicRoute path="/request-password-reset" component={RequestPasswordReset} />
            <PublicRoute path="/password-reset/:tempPass" component={ResetPassword} />
            <PublicRoute path="/verify" component={Verify} />
            <PrivateRoute path="/life-shared/timeline/" component={Log} />
            <PrivateRoute path="/life-shared/timeline" component={Log} />
            <PrivateRoute path="/life-shared/gallery/" component={Log} />
            <PrivateRoute path="/life-shared/list/" component={Log} />
            <PrivateRoute path="/life-shared/people/" component={Log} />
            <PrivateRoute path="/life-shared/people" component={Log} />
            <PrivateRoute path="/life-shared/category/" component={Log} />
            <PrivateRoute path="/life-shared/category" component={Log} />
            <PrivateRoute path="/life-shared/drafts" component={Log} />
            <PrivateRoute path="/life-shared/posts/preview" component={Preview} />
            <PrivateRoute path="/life-shared/posts/:postId" component={PostDetail} />
            <PrivateRoute path="/manage/my-account" component={MyAccount} />
            <PrivateRoute path="/profile-alt" component={ComingSoon} />
            <PrivateRoute path="/growth/" component={Growth} />
            <PrivateRoute path="/nest/" component={Nest} />
            <PrivateRoute path="/resource-center" component={ResourceCenter} />
            <PrivateRoute path="/love/" component={Love} />
            <PrivateRoute path="/add-mobile" component={AddMobile} />
            <PrivateRoute path="/create-profile" component={CreateProfile} />
            <PrivateRoute path="/verify" component={Verify} />
            <PrivateRoute path="/my-riza/" component={MyRiza} />
            <PrivateRoute path="/manage/logs" component={ManageLogs} />
            <PrivateRoute path="/password-reset" component={ResetPassword} />
            <Route path="/logout" component={Logout} />
            <Route path="/" exact component={Home} />
            <Route path="" exact component={Home} />
            <Route component={NoMatch} />
          </Switch>
        </Layout>
      </div>
    </ThemeProvider>
  );
};

export default App;
